/**
 * http — the builder's authenticated fetch wrapper (build-plan §3.6).
 *
 * Two jobs on every API call, ported from the dashboard's proven interceptor pattern:
 *  1. Attach `Authorization: Bearer <in-memory token>` + `credentials: 'include'` (the refresh
 *     cookie rides along on a same-site call).
 *  2. On a 401 from an authenticated call → ONE silent refresh, then replay the request once. A second
 *     401 → SessionExpiredError (the boot/UI bounces to dashboard login).
 *
 * The refresh is SINGLE-FLIGHT (build-plan §3.6, D8): concurrent 401s (e.g. a save + a publish firing
 * together) must share ONE `/refresh` POST. Refresh-token ROTATION + reuse-detection (ADR-0001) mean a
 * second concurrent refresh would present an already-rotated token and revoke the whole token family —
 * so we cache the in-flight promise and clear it when it settles, exactly like the dashboard `AuthApi`.
 *
 * Tenancy (build-plan §10.1): the builder NEVER sends `store_id`/`tenant` in any request — the backend
 * derives it from the JWT. This module only takes a path + query + body; there is no store-id surface.
 */
import { isReadOnlyView } from '../boot/view-handoff';
import { clearToken, getStoreId, getToken, setToken } from '../boot/session';
import { isStoreDrifted, markStoreDrifted, tokenLeftThePin } from '../boot/storePin';
import { deriveApiBaseUrl } from '../boot/config';
import {
  HttpError,
  NetworkError,
  ReadOnlyViewError,
  SessionExpiredError,
  StoreChangedError,
} from './errors';
import { pushToast } from '../ui/toastStore';
import { bt, btp } from '../i18n';

const REFRESH_PATH = '/api/auth/seller/refresh';

let inFlightRefresh: Promise<boolean> | null = null;

/** Coalesce the "read-only" toast so a burst of blocked writes shows one message, not a stack. */
let lastReadOnlyToastAt = 0;
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Exchange the HttpOnly refresh cookie for a fresh access token. Resolves true on success (token
 * stashed in memory), false on any failure (token cleared). Never throws. Single-flight: concurrent
 * callers share the one in-flight POST.
 */
export function refreshSession(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      const res = await fetch(`${deriveApiBaseUrl()}${REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        clearToken();
        return false;
      }
      const data = (await res.json()) as { accessToken?: string };
      if (!data.accessToken) {
        clearToken();
        return false;
      }
      setToken(data.accessToken);
      return true;
    } catch {
      clearToken();
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

export interface RequestOptions {
  method?: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE';
  /** Query params appended to the path (template/handle/locale only — never a store id). */
  query?: Record<string, string>;
  /** JSON body (PUT/POST). */
  body?: unknown;
}

function buildUrl(path: string, query?: Record<string, string>): string {
  const base = `${deriveApiBaseUrl()}${path}`;
  if (!query || Object.keys(query).length === 0) return base;
  const qs = new URLSearchParams(query).toString();
  return `${base}?${qs}`;
}

async function doFetch(path: string, opts: RequestOptions): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  try {
    return await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new NetworkError();
  }
}

/** Parse a JSON body, tolerating an empty one (204 / no content). */
async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Run a request thunk with the single-flight refresh + one-401-replay policy, returning the final
 * `Response`. The thunk MUST build a fresh request each call so the replay picks up the refreshed
 * Bearer token (and so a one-shot body — FormData/Blob — is rebuilt). Shared by `apiRequest` (JSON)
 * and `uploadAsset` (multipart / octet-stream) so the auth policy lives in exactly one place.
 * Throws `SessionExpiredError` when the refresh fails or the replayed call 401s again.
 */
export async function withAuthRetry(
  send: () => Promise<Response>,
  method: string = 'GET',
): Promise<Response> {
  // SECURITY (superadmin read-only consultation): refuse EVERY write before it hits the network — no
  // autosave, no publish, no create-template, no upload, no anything. One choke point covering every
  // caller, mirroring the server's AdminStoreViewReadOnlyFilter. GET/HEAD/OPTIONS pass through.
  const readOnly = isReadOnlyView();
  if (!SAFE_METHODS.has(method.toUpperCase()) && readOnly) {
    const now = Date.now();
    if (now - lastReadOnlyToastAt > 2000) {
      lastReadOnlyToastAt = now;
      pushToast(
        bt('chrome.view.readonly_toast', 'Read-only consultation: nothing is saved.'),
        'info',
      );
    }
    throw new ReadOnlyViewError();
  }
  // Same choke point, the other tenancy accident (founder 2026-09-17): the ACCOUNT has switched store
  // under this open document. Every write from here on would land in the shop the seller moved INTO,
  // carrying the page they are looking at in the shop they moved OUT of. Reads are left alone — the UI
  // is already blocked, and a read can only ever return what this document's own token is scoped to.
  if (!SAFE_METHODS.has(method.toUpperCase()) && isStoreDrifted()) {
    throw new StoreChangedError();
  }
  let res = await send();
  if (res.status === 401) {
    // A consultation token has NO refresh cookie of its own, but the browser may hold a real seller
    // cookie. Refreshing here would swap the read-only token for a full WRITE session (the cookie
    // escape) and silently lift every read-only gate. So in a consultation we NEVER refresh: the
    // expired consultation just ends.
    if (readOnly) {
      clearToken();
      throw new SessionExpiredError();
    }
    const ok = await refreshSession();
    if (!ok) throw new SessionExpiredError();
    // THE SILENT ONE. The refresh cookie is shared by the whole account and always mints the store that
    // is active NOW — so a tab that has been sitting on store A since before a switch gets a token for
    // store B here, and the replay below would send A's page into B. Refuse the replay and latch the
    // drift instead: the request fails once, loudly, rather than succeeding in the wrong shop.
    if (tokenLeftThePin()) {
      markStoreDrifted({ id: getStoreId(), name: null });
      throw new StoreChangedError();
    }
    res = await send();
    if (res.status === 401) {
      clearToken();
      throw new SessionExpiredError();
    }
  }
  return res;
}

/**
 * Authenticated request with single-flight refresh + one 401 replay. Returns the parsed JSON body on
 * 2xx (or undefined on 204). Throws `HttpError` (status + body) on a non-2xx; `SessionExpiredError`
 * when the refresh itself fails or a replayed call 401s again; `NetworkError` on transport failure.
 * Callers (builderApi) map specific 409 codes to ConflictError / NoStoreError.
 */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await withAuthRetry(() => doFetch(path, opts), opts.method ?? 'GET');

  const body = await parseBody(res);
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : btp('chrome.err.request_failed', 'Request failed ({status})', {
            status: res.status,
          });
    throw new HttpError(res.status, message, body);
  }
  return body as T;
}

/** Extract the `{code}` field from an error envelope body, when present. */
export function errorCode(body: unknown): string | null {
  if (typeof body === 'object' && body !== null && 'code' in body) {
    const code = (body as { code: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}
